/**
 * EL ALMACÉN SIN CONEXIÓN: lo que la app guarda en el teléfono para poder
 * enseñarlo sin red, como WhatsApp enseña una conversación de hace años.
 *
 * Una sola base IndexedDB (`ulture_offline`) con cinco cajones:
 *
 *   · `tablas`     — la COPIA de las tablas de la empresa (toda la historia que
 *                     el usuario puede leer), una entrada por tabla. De aquí
 *                     contesta `consultaLocal` cualquier consulta sin red.
 *   · `consultas`  — el resultado de cada consulta de React Query (lo que pinta
 *                     una pantalla), para abrirla AL INSTANTE con lo último.
 *   · `respuestas` — la respuesta HTTP de cada lectura a la base (PostgREST),
 *                     incluidas las RPC de lectura, que la copia no sabe calcular.
 *   · `archivos`   — fotos y audios de muestreos y teselas del satélite (Blob).
 *   · `meta`       — sellos sueltos (última descarga completa, etc.).
 *
 * TODO VA POR USUARIO. Cada clave empieza por `<user_id>|`, así que borrar lo de
 * una persona es borrar un rango, y lo de otra persona nunca se lee: al cerrar
 * sesión se borra lo suyo, y al entrar alguien distinto en el mismo aparato se
 * borra lo de los demás (`borrarOtrosUsuarios`).
 *
 * NUNCA LANZA. Sin IndexedDB (modo privado, cuota llena, WebView raro) cada
 * función contesta «no hay nada» y la app sigue funcionando como antes, solo
 * que sin guardar.
 */

import { modoActivo } from "./estadoOffline";

const DB_NAME = "ulture_offline";
const DB_VERSION = 1;

export type Cajon = "tablas" | "consultas" | "respuestas" | "archivos" | "meta";
const CAJONES: Cajon[] = ["tablas", "consultas", "respuestas", "archivos", "meta"];

/** El separador entre el usuario y la clave. No aparece en un uuid. */
const SEP = "|";
/** Mayor que cualquier carácter de una clave: cierra el rango de un usuario. */
const FIN = "￿";

let abriendo: Promise<IDBDatabase | null> | null = null;

function abrir(): Promise<IDBDatabase | null> {
  if (abriendo) return abriendo;
  abriendo = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const c of CAJONES) {
          if (!db.objectStoreNames.contains(c)) db.createObjectStore(c);
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        /* Otra pestaña (o una versión nueva de la app) quiere subir la versión:
           se cierra para no bloquearla, y la próxima llamada vuelve a abrir. */
        db.onversionchange = () => {
          try {
            db.close();
          } catch {
            /* nada */
          }
          abriendo = null;
        };
        db.onclose = () => {
          abriendo = null;
        };
        resolve(db);
      };
      req.onerror = () => {
        abriendo = null;
        resolve(null);
      };
      req.onblocked = () => resolve(null);
    } catch {
      abriendo = null;
      resolve(null);
    }
  });
  return abriendo;
}

export function claveDe(uid: string, clave: string): string {
  return `${uid}${SEP}${clave}`;
}

function rangoDe(uid: string): IDBKeyRange | null {
  try {
    return IDBKeyRange.bound(`${uid}${SEP}`, `${uid}${SEP}${FIN}`);
  } catch {
    return null;
  }
}

export async function leer<T>(cajon: Cajon, uid: string, clave: string): Promise<T | null> {
  const db = await abrir();
  if (!db) return null;
  return new Promise<T | null>((resolve) => {
    try {
      const req = db.transaction(cajon, "readonly").objectStore(cajon).get(claveDe(uid, clave));
      req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Guarda. Si el valor no se puede clonar o no cabe, se calla y devuelve false.
 *
 * Con el interruptor APAGADO no guarda nada: es el único sitio por el que se escribe,
 * así que un guardado que estuviera en vuelo al apagarse (una respuesta que llega
 * tarde, una instantánea programada) no puede volver a dejar datos después del
 * borrado (`apagarModoSinConexion` apaga ANTES de borrar).
 */
export async function guardar(cajon: Cajon, uid: string, clave: string, valor: unknown): Promise<boolean> {
  if (!modoActivo()) return false;
  const db = await abrir();
  if (!modoActivo()) return false;
  if (!db) return false;
  return new Promise<boolean>((resolve) => {
    try {
      const tx = db.transaction(cajon, "readwrite");
      tx.objectStore(cajon).put(valor, claveDe(uid, clave));
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export async function borrar(cajon: Cajon, uid: string, clave: string): Promise<void> {
  const db = await abrir();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(cajon, "readwrite");
      tx.objectStore(cajon).delete(claveDe(uid, clave));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** ¿Qué claves (sin el prefijo del usuario) hay en un cajón? */
export async function clavesDe(cajon: Cajon, uid: string): Promise<string[]> {
  const db = await abrir();
  const rango = rangoDe(uid);
  if (!db || !rango) return [];
  return new Promise<string[]>((resolve) => {
    try {
      const req = db.transaction(cajon, "readonly").objectStore(cajon).getAllKeys(rango);
      req.onsuccess = () => {
        const corte = uid.length + SEP.length;
        resolve((req.result as IDBValidKey[]).map((k) => String(k).slice(corte)));
      };
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

/** Todo lo de un usuario, en todos los cajones. Para «Cerrar sesión». */
export async function borrarUsuario(uid: string): Promise<void> {
  const db = await abrir();
  const rango = rangoDe(uid);
  if (!db || !rango) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(CAJONES, "readwrite");
      for (const c of CAJONES) tx.objectStore(c).delete(rango);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Todo lo que NO es de este usuario. Un aparato guarda los datos de una sola
 * persona: si entra otra, lo del anterior se va (nunca se le enseñaría, porque
 * las claves van por usuario, pero tampoco tiene por qué quedarse ocupando sitio
 * ni a la vista de quien abra la base).
 */
export async function borrarOtrosUsuarios(uid: string): Promise<void> {
  const db = await abrir();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(CAJONES, "readwrite");
      for (const c of CAJONES) {
        const store = tx.objectStore(c);
        // Antes del rango del usuario y después de él.
        store.delete(IDBKeyRange.upperBound(`${uid}${SEP}`, true));
        store.delete(IDBKeyRange.lowerBound(`${uid}${SEP}${FIN}`, true));
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Lee varios valores de golpe (una transacción), en el orden pedido. */
export async function leerVarios<T>(cajon: Cajon, uid: string, claves: string[]): Promise<(T | null)[]> {
  if (claves.length === 0) return [];
  const db = await abrir();
  if (!db) return claves.map(() => null);
  return new Promise<(T | null)[]>((resolve) => {
    try {
      const store = db.transaction(cajon, "readonly").objectStore(cajon);
      const out: (T | null)[] = new Array(claves.length).fill(null);
      let pendientes = claves.length;
      claves.forEach((c, i) => {
        const req = store.get(claveDe(uid, c));
        req.onsuccess = () => {
          out[i] = (req.result as T | undefined) ?? null;
          if (--pendientes === 0) resolve(out);
        };
        req.onerror = () => {
          if (--pendientes === 0) resolve(out);
        };
      });
    } catch {
      resolve(claves.map(() => null));
    }
  });
}

/**
 * Pide al sistema que no borre esta base cuando ande corto de espacio. En iOS
 * (WKWebView) no siempre se concede, pero no cuesta nada pedirlo; una vez por
 * arranque basta.
 */
let persistenciaPedida = false;
export function pedirAlmacenPersistente(): void {
  if (persistenciaPedida) return;
  persistenciaPedida = true;
  try {
    void navigator.storage?.persist?.().catch(() => undefined);
  } catch {
    /* nada */
  }
}

/** Para la ficha «Datos guardados en este móvil»: cuánto ocupa todo, aproximado. */
export async function espacioUsado(): Promise<{ usado: number | null; cuota: number | null }> {
  try {
    const est = await navigator.storage?.estimate?.();
    return { usado: est?.usage ?? null, cuota: est?.quota ?? null };
  } catch {
    return { usado: null, cuota: null };
  }
}
